<template>
  <v-row justify="center">
    <v-card class="transparent my-3 mx-auto" rounded outlined width="900">
      <v-card-text style="height: calc(100vh - 175px); overflow-y: auto;">
        <div class="mb-6">
          <img :src="require('@/assets/appLogo-color--Onlight.svg')" width="100" class="mb-4" />
          <span class="d-block text-h4 font-weight-bold">{{ title }}</span>
          <span class="d-block body-1 text--primary">{{ subtitle }}</span>
        </div>

        <v-row class="mt-6">
          <!-- before -->
          <v-col cols="6">
            <v-chip class="mx-4" color="secondary" label>
              <v-icon left small>mdi-clock</v-icon>Sin Piriod
            </v-chip>
            <v-card outlined class="mt-2 px-4 py-0 rounded-lg" style="border-color: #E0E0E0;">
              <VistaFlowDiagram :steps="beforeSteps" />
            </v-card>
          </v-col>
          <!-- end before -->

          <!-- after -->
          <v-col cols="6">
            <v-chip class="mx-4" color="primary" label dark>
              <v-icon left small>mdi-check-circle</v-icon>Con Piriod
            </v-chip>
            <v-card outlined class="mt-2 px-4 py-0 rounded-lg" style="border-color: #3F51B5;">
              <VistaFlowDiagram :steps="afterSteps" :highlight="true" />
            </v-card>
          </v-col>
          <!-- end after -->

        </v-row>
      </v-card-text>

      <v-card-actions class="pa-10">
        <v-spacer />
        <v-btn @click="exploreTestMode" class="pr-4 pl-6" color="primary" large :ripple="false">
          Comenzar a explorar<v-icon right size="22">mdi-arrow-right</v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-row>
</template>

<script>
import { mapState } from 'vuex'
import GenericViewMixin from '@/mixins/GenericViewMixin'
import VistaFlowDiagram from '@/components/onboarding/VistaFlowDiagram'

export default {
  mixins: [GenericViewMixin],
  components: { VistaFlowDiagram },
  computed: {
    ...mapState({
      path: state => state.onboarding.path,
      responses: state => state.onboarding.responses,
      completed: state => state.onboarding.completed
    }),
    title () {
      const titles = {
        subscription_card: 'Tus cobros recurrentes en piloto automático',
        subscription_transfer: 'Nunca más perseguir transferencias en una planilla',
        invoicing_only: 'Factura, cobra y concilia desde un solo lugar'
      }
      return titles[this.path] || 'Tu flujo personalizado en Piriod'
    },
    subtitle () {
      const subtitles = {
        subscription_card: 'Esto es lo que Piriod hace por ti, según lo que nos contaste.',
        subscription_transfer: 'Así se vería tu proceso de facturación, cobro y conciliación bancaria con Piriod.',
        invoicing_only: 'Así se vería tu proceso de facturación y cobro centralizado con Piriod.'
      }
      return subtitles[this.path] || 'Así se vería tu proceso ordenado con Piriod.'
    },
    beforeSteps () {
      if (this.path === 'subscription_card') {
        return [
          { icon: 'mdi-table', title: 'Control de clientes en planilla', description: 'Lista de clientes en Excel o Google Sheets' },
          { icon: 'mdi-file-document-edit', title: 'Facturación manual', description: 'Crear facturas una por una' },
          { icon: 'mdi-credit-card-clock', title: 'Cobro manual con tarjeta', description: 'Generar cobros directo en el procesador' },
          { icon: 'mdi-email', title: 'Notificación manual', description: 'Enviar emails cuando el pago falla' },
          { icon: 'mdi-chart-line', title: 'Métricas a mano', description: 'Calcular MRR y churn en una planilla aparte' }
        ]
      }

      if (this.path === 'subscription_transfer') {
        return [
          { icon: 'mdi-file-document-edit', title: 'Facturación manual', description: 'Crear facturas una por una' },
          { icon: 'mdi-email', title: 'Envío manual al cliente', description: 'Adjuntar factura en un email' },
          { icon: 'mdi-phone', title: 'Perseguir pagos', description: 'Llamar o escribir cuando no pagan a tiempo' },
          { icon: 'mdi-bank', title: 'Conciliación manual', description: 'Revisar cartola bancaria y cruzar con facturas en Excel' },
          { icon: 'mdi-file-check', title: 'Recibo manual', description: 'Generar y enviar recibos a mano' }
        ]
      }

      // invoicing_only
      return [
        { icon: 'mdi-file-document-edit', title: 'Facturación manual', description: 'Crear cada factura individualmente' },
        { icon: 'mdi-email', title: 'Envío por email', description: 'Adjuntar y enviar facturas manualmente' },
        { icon: 'mdi-cash-clock', title: 'Cobro ad-hoc', description: 'Esperar transferencias o enviar datos bancarios' },
        { icon: 'mdi-bank', title: 'Conciliación manual', description: 'Cruzar pagos con facturas en Excel' }
      ]
    },
    afterSteps () {
      if (this.path === 'subscription_card') {
        return [
          { icon: 'mdi-account-multiple', title: 'Tus clientes centralizados', description: 'Base de datos unificada de clientes' },
          { icon: 'mdi-tag-multiple', title: 'Planes configurados', description: 'Precios y tarifas listas para asignar' },
          { icon: 'mdi-autorenew', title: 'Suscripción automática', description: 'Ciclo de cobro recurrente sin intervención' },
          { icon: 'mdi-file-document-check', title: 'Factura automática', description: 'Emisión tributaria al momento del cobro' },
          { icon: 'mdi-credit-card-check', title: 'Cobro automático', description: 'Cargo a tarjeta con reintentos inteligentes' },
          { icon: 'mdi-bell', title: 'Notificación automática', description: 'El cliente recibe aviso de cobro y factura' }
        ]
      }

      if (this.path === 'subscription_transfer') {
        return [
          { icon: 'mdi-account-multiple', title: 'Tus clientes centralizados', description: 'Base de datos unificada de clientes' },
          { icon: 'mdi-autorenew', title: 'Suscripción activa', description: 'Ciclo de facturación recurrente automático' },
          { icon: 'mdi-file-document-check', title: 'Factura automática', description: 'Emisión y envío al cliente sin intervención' },
          { icon: 'mdi-bell-ring', title: 'Recordatorio antes del vencimiento', description: 'Notificación automática N días antes' },
          { icon: 'mdi-bank-transfer', title: 'Transferencia del cliente', description: 'El cliente paga a tu cuenta bancaria' },
          { icon: 'mdi-check-decagram', title: 'Conciliación automática', description: 'Piriod cruza el pago con la factura' },
          { icon: 'mdi-receipt-text-check', title: 'Recibo de pago', description: 'Generado y enviado automáticamente' }
        ]
      }

      // invoicing_only
      return [
        { icon: 'mdi-account-multiple', title: 'Tus clientes centralizados', description: 'Base de datos unificada de clientes' },
        { icon: 'mdi-file-document-check', title: 'Factura desde Piriod', description: 'Emisión individual o por lote' },
        { icon: 'mdi-link-variant', title: 'Link de pago incluido', description: 'Cada factura lleva su link de cobro' },
        { icon: 'mdi-cash-check', title: 'Pago del cliente', description: 'Por transferencia o checkout con tarjeta' },
        { icon: 'mdi-check-decagram', title: 'Conciliación automática', description: 'Piriod cruza pagos con facturas' },
        { icon: 'mdi-receipt-text-check', title: 'Recibo automático', description: 'Generado al confirmar el pago' }
      ]
    }
  },
  created () {
    if (!this.completed) {
      this.$router.push({ name: 'OrganizationOnboarding' })
    }

    this.trackEvent('Vista Orquestada Viewed', { path: this.path })
  },
  methods: {
    exploreTestMode () {
      this.$store.commit('onboarding/SET_VISTA_ORQUESTADA_SEEN', true)
      this.$store.dispatch('onboarding/SAVE_STATE')
      this.trackEvent('Vista Orquestada CTA Clicked', { path: this.path, cta: 'explore' })
      this.$store.commit('ui/SET_TEST_MODE', true)
      this.$store.dispatch('onboarding/SEED_TEST_DATA')
      this.$store.commit('ui/TOGGLE_QUICKSTART_DRAWER', true)
      this.$router.push({ name: 'AccountDashboard' })
    },
    scheduleCall () {
      this.$store.commit('onboarding/SET_VISTA_ORQUESTADA_SEEN', true)
      this.$store.dispatch('onboarding/SAVE_STATE')
      this.trackEvent('Vista Orquestada CTA Clicked', { path: this.path, cta: 'activate' })
      this.intercomNewMessage('Hola, quiero activar mi cuenta en Piriod.')
      this.$store.commit('ui/TOGGLE_QUICKSTART_DRAWER', true)
      this.$router.push({ name: 'AccountDashboard' })
    }
  }
}
</script>
<style>
.v-timeline::before {
  width: 26px !important;
  margin-left: -13px !important;
  background-color: rgba(64, 80, 180, 0.1) !important;
}
</style>